import { useState, useContext, useEffect, useRef } from 'react';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, Typography, InputAdornment } from '@mui/material';
import { Box } from '@mui/system';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { Formik, Form, Field } from 'formik';
import { TextField } from 'formik-mui';
import * as Yup from 'yup';

import { Businesses } from 'lib/api';
import { CustomerContext } from 'context/customers/customersContext';
import { CUSTOMERS_ACTIONS } from 'context/customers/customersReducer';
import { numberFormat } from 'helpers/i18n';
import SnackBarComponent from 'components/shared/SnackBarComponent';
import './EditSpendLimitModal.css';

const { HIDE_EDIT_SPEND_LIMIT_MODAL, RELOAD_CUSTOMERS } = CUSTOMERS_ACTIONS;
const INITIAL_VALUES = {
    spendLimit: ''
};
const UPDATE_SPEND_LIMIT_SCHEMA = Yup.object().shape({
    spendLimit: Yup.number().positive('Invalid amount').required('Required')
});

const EditSpendLimitModal = () => {
    const {
        state: { isEditSpendLimitModalOpen, selectedCustomer },
        dispatch
    } = useContext(CustomerContext);
    const [errorMessage, setErrorMessage] = useState('');
    const SnackBarRef = useRef(null);

    useEffect(() => {
        if (!isEditSpendLimitModalOpen) {
            setErrorMessage('');
        }
    }, [isEditSpendLimitModalOpen]);

    const handleCancelClick = () => {
        dispatch({ type: HIDE_EDIT_SPEND_LIMIT_MODAL });
    };

    const handleConfirmBtnClick = async (values, actions) => {
        setErrorMessage('');
        try {
            const body = {
                creditLimit: values.spendLimit
            };
            await Businesses.updateSpendLimit(selectedCustomer.id_business, body);

            SnackBarRef.current.openSnackBar({
                label: 'Spend limit update successful',
                severity: 'success'
            });
            dispatch({ type: HIDE_EDIT_SPEND_LIMIT_MODAL });
            dispatch({ type: RELOAD_CUSTOMERS });
        } catch (error) {
            setErrorMessage(error?.response?.data?.message || 'An unexpected error ocurred. Please try again.');
        }
    };

    return (
        <>
            <Dialog open={isEditSpendLimitModalOpen} onClose={() => {}} maxWidth="xs">
                <DialogContent sx={{ padding: '32px', width: '384px' }}>
                    <Box sx={{ marginBottom: '32px' }}>
                        <Box>
                            <Typography className="data-label">Business ID:</Typography>
                            <Typography className="text-secondary">{selectedCustomer?.id_business}</Typography>
                        </Box>
                        <Box>
                            <Typography className="data-label">Business name:</Typography>
                            <Typography className="text-secondary">{selectedCustomer?.legal_name}</Typography>
                        </Box>
                        <Box>
                            <Typography className="data-label">Admin name:</Typography>
                            <Typography className="text-secondary">
                                {selectedCustomer?.first_name} {selectedCustomer?.last_name}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography className="data-label">Current spend limit:</Typography>
                            <Typography className="text-secondary">
                                {numberFormat(selectedCustomer?.credit_limit || 0)}
                            </Typography>
                        </Box>
                    </Box>
                    <Formik
                        initialValues={INITIAL_VALUES}
                        validationSchema={UPDATE_SPEND_LIMIT_SCHEMA}
                        onSubmit={handleConfirmBtnClick}>
                        {({ isSubmitting, isValid, dirty, values }) => (
                            <Form>
                                <Box sx={{ paddingBottom: '10px' }}>
                                    <Typography className="form-title">Change Spend Limit</Typography>
                                    <Typography className="text-secondary">Assign account new spend limit</Typography>
                                    <Field
                                        type="text"
                                        name="spendLimit"
                                        component={TextField}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <AttachMoneyIcon fontSize="small" />
                                                </InputAdornment>
                                            )
                                        }}
                                        placeholder="Enter credit limit"
                                        margin="dense"
                                        fullWidth
                                        size="small"
                                    />
                                </Box>
                                {errorMessage && <Typography className="text-error">{errorMessage}</Typography>}
                                <DialogActions sx={{ marginTop: '32px', padding: '0px' }}>
                                    <Button
                                        variant="outlined"
                                        sx={{ color: '#7D95A5', borderColor: '#7D95A5' }}
                                        onClick={handleCancelClick}
                                        disabled={isSubmitting}>
                                        Cancel
                                    </Button>

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={(!dirty && values.spendLimit === '') || !isValid || isSubmitting}>
                                        {isSubmitting ? <CircularProgress size="20px" /> : 'Confirm'}
                                    </Button>
                                </DialogActions>
                            </Form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>

            <SnackBarComponent ref={SnackBarRef} />
        </>
    );
};

export default EditSpendLimitModal;
